import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, IconButton, Tooltip } from '@mui/material';
import DraftEditor from './DraftEditor';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const Interpretations = ({ updateInterpretations, interpretations }) => {
    const [interpretationName, setInterpretationName] = useState('');
    const [minScore, setMinScore] = useState('');
    const [maxScore, setMaxScore] = useState('');
    const [description, setDescription] = useState('');
    const [interpretationsList, setInterpretationsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        minScore: '',
        maxScore: '',
        description: ''
    });

    useEffect(() => {
        if (interpretations && interpretations.length > 0) {
            setInterpretationsList(interpretations);
        }
    }, [interpretations]);

    const handleSort = (column) => {
        const isAsc = sortBy === column && sortOrder === 'asc';
        setSortBy(column);
        setSortOrder(isAsc ? 'desc' : 'asc');
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        
        if (!interpretationName || !minScore || !maxScore || !description) {
            alert('Please fill all fields');
            return;
        }
        
        if (parseInt(minScore) > parseInt(maxScore)) {
            alert('Min score cannot be greater than max score');
            return;
        }

        setLoading(true);

        const newInterpretation = {
            name: interpretationName,
            minScore: parseInt(minScore),
            maxScore: parseInt(maxScore),
            description: description
        };

        const updatedInterpretations = [...interpretationsList, newInterpretation];
        setInterpretationsList(updatedInterpretations);
        updateInterpretations(updatedInterpretations);
        setLoading(false);
        clearFormData();
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setEditFormData({
            name: interpretationsList[index].name,
            minScore: interpretationsList[index].minScore.toString(),
            maxScore: interpretationsList[index].maxScore.toString(),
            description: interpretationsList[index].description
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: value
        });
    };

    const handleSaveEdit = () => {
        if (!editFormData.name || !editFormData.minScore || !editFormData.maxScore || !editFormData.description) {
            alert('Please fill all fields');
            return;
        }
        
        if (parseInt(editFormData.minScore) > parseInt(editFormData.maxScore)) {
            alert('Min score cannot be greater than max score');
            return;
        }

        const updatedInterpretations = [...interpretationsList];
        updatedInterpretations[editingIndex] = {
            name: editFormData.name,
            minScore: parseInt(editFormData.minScore),
            maxScore: parseInt(editFormData.maxScore),
            description: editFormData.description
        };

        setInterpretationsList(updatedInterpretations);
        updateInterpretations(updatedInterpretations);
        setEditingIndex(null);
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
    };

    const handleDelete = (index) => {
        const updatedInterpretations = [...interpretationsList];
        updatedInterpretations.splice(index, 1);
        setInterpretationsList(updatedInterpretations);
        updateInterpretations(updatedInterpretations);
        
        // If we're deleting the item being edited, cancel edit mode
        if (editingIndex === index) {
            setEditingIndex(null);
        }
    };

    const clearFormData = () => {
        setInterpretationName('');
        setMinScore('');
        setMaxScore('');
        setDescription('');
    };

    const sortedInterpretations = [...interpretationsList].sort((a, b) => {
        if (!sortBy) return 0;
        if (sortBy === 'minScore' || sortBy === 'maxScore') {
            return sortOrder === 'asc' 
                ? a[sortBy] - b[sortBy] 
                : b[sortBy] - a[sortBy];
        }
        return sortOrder === 'asc' 
            ? a[sortBy].localeCompare(b[sortBy]) 
            : b[sortBy].localeCompare(a[sortBy]);
    });

    return (
        <div style={{ padding: '16px' }}>
            <Typography variant="h6" gutterBottom>Interpretation Details</Typography>
            
            <Grid container spacing={2} style={{ marginBottom: '16px' }}>
                <Grid item xs={12} md={4}>
                    <TextField 
                        label="Interpretation Name" 
                        value={interpretationName} 
                        onChange={(e) => setInterpretationName(e.target.value)} 
                        fullWidth 
                        required
                    />
                </Grid>
                <Grid item xs={6} md={4}>
                    <TextField 
                        label="Min Score" 
                        type="number"
                        value={minScore} 
                        onChange={(e) => setMinScore(e.target.value)} 
                        fullWidth 
                        required
                    />
                </Grid>
                <Grid item xs={6} md={4}>
                    <TextField 
                        label="Max Score" 
                        type="number"
                        value={maxScore} 
                        onChange={(e) => setMaxScore(e.target.value)} 
                        fullWidth 
                        required
                    />
                </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom>Description</Typography>
            <DraftEditor 
                handleDescription={(data) => setDescription(data)} 
                description={description} 
                clearEditor={!description} 
            />

            <Button 
                onClick={handleSubmit} 
                variant="contained" 
                color="primary" 
                style={{ marginTop: '16px', marginBottom: '24px', background: ' #0a0068' }}
                disabled={loading}
            >
                {loading ? 'Submitting...' : 'Add Interpretation'}
            </Button>

            {interpretationsList.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortBy === 'name'}
                                        direction={sortBy === 'name' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('name')}
                                    >
                                        Interpretation Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortBy === 'minScore'}
                                        direction={sortBy === 'minScore' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('minScore')}
                                    >
                                        Min Score
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortBy === 'maxScore'}
                                        direction={sortBy === 'maxScore' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('maxScore')}
                                    >
                                        Max Score
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedInterpretations.map((item, index) => (
                                <TableRow key={index}>
                                    {editingIndex === index ? (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    value={editFormData.name}
                                                    onChange={(e) => handleEditChange('name', e.target.value)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    value={editFormData.minScore}
                                                    onChange={(e) => handleEditChange('minScore', e.target.value)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    value={editFormData.maxScore}
                                                    onChange={(e) => handleEditChange('maxScore', e.target.value)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <DraftEditor
                                                    handleDescription={(data) => handleEditChange('description', data)}
                                                    description={editFormData.description}
                                                    clearEditor={false}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={handleSaveEdit} color="primary">
                                                    <SaveIcon />
                                                </IconButton>
                                                <IconButton onClick={handleCancelEdit} color="secondary">
                                                    <CancelIcon />
                                                </IconButton>
                                            </TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell>{item.name}</TableCell>
                                            <TableCell>{item.minScore}</TableCell>
                                            <TableCell>{item.maxScore}</TableCell>
                                            <TableCell>
                                                <Tooltip 
                                                    title={<div dangerouslySetInnerHTML={{ __html: item.description }} />} 
                                                    placement="right"
                                                    arrow
                                                >
                                                    <IconButton>
                                                        <InfoIcon color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => handleEdit(index)} color="primary">
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton onClick={() => handleDelete(index)} color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    );
};

export default Interpretations;